/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 * 
 ********************************************************************!*/

import { IEntity, IIntent, IModel, IVersion } from "@chatcaptain/types/chatbot";
import { IFlow } from "@chatcaptain/types/dialog";
import { MutationTree } from "vuex";
import { IBotState } from "./state";

export enum Mutations {
  SET_ID = "SET_ID",
  SET_NAME = "SET_NAME",
  SET_LANGUAGE = "SET_LANGUAGE",
  SET_DEFAULT_LANGUAGE = "SET_DEFAULT_LANGUAGE",
  SET_LANGUAGES = "SET_LANGUAGES",
  SET_FLOWS = "SET_FLOWS",
  SET_FAQS = "SET_FAQS",
  SET_ENTITIES = "SET_ENTITIES",
  SET_MODELS = "SET_MODELS",
  SET_VERSIONS = "SET_VERSIONS",
  SET_LIVE_VERSION = "SET_LIVE_VERSION",
  SET_LAST_VERSION = "SET_LAST_VERSION"
}

export interface IMutations<S = IBotState> {
  [Mutations.SET_ID](state: S, payload: string): void;
  [Mutations.SET_NAME](state: S, payload: string): void;
  [Mutations.SET_LANGUAGE](state: S, payload: string): void;
  [Mutations.SET_DEFAULT_LANGUAGE](state: S, payload: string): void;
  [Mutations.SET_LANGUAGES](state: S, payload: string[]): void;
  [Mutations.SET_FLOWS](state: S, payload: IFlow[]): void;
  [Mutations.SET_FAQS](state: S, payload: IIntent[]): void;
  [Mutations.SET_ENTITIES](state: S, payload: IEntity[]): void;
  [Mutations.SET_MODELS](state: S, payload: IModel[]): void;
  [Mutations.SET_VERSIONS](state: S, payload: IVersion[]): void;
  [Mutations.SET_LIVE_VERSION](state: S, payload: number): void;
  [Mutations.SET_LAST_VERSION](state: S, payload: IVersion): void;
}

export const mutations: MutationTree<IBotState> & IMutations = {
  [Mutations.SET_ID]: (state: IBotState, id: string) => {
    state.id = id;
  },
  [Mutations.SET_NAME]: (state: IBotState, name: string) => {
    state.name = name;
  },
  [Mutations.SET_LANGUAGE]: (state: IBotState, language: string) => {
    state.language = language;
  },
  [Mutations.SET_DEFAULT_LANGUAGE]: (state: IBotState, language: string) => {
    state.defaultLanguage = language;
  },
  [Mutations.SET_LANGUAGES]: (state: IBotState, languages: string[]) => {
    state.languages = languages;
  },
  [Mutations.SET_FLOWS]: (state: IBotState, flows: IFlow[]) => {
    state.flows = flows;
  },
  [Mutations.SET_FAQS]: (state: IBotState, faqs: IIntent[]) => {
    state.faqIntents = faqs;
  },
  [Mutations.SET_ENTITIES]: (state: IBotState, entities: IEntity[]) => {
    state.entities = entities;
  },
  [Mutations.SET_MODELS]: (state: IBotState, models: IModel[]) => {
    state.models = models;
  },
  [Mutations.SET_VERSIONS]: (state: IBotState, versions: IVersion[]) => {
    state.versions = versions;
  },
  [Mutations.SET_LIVE_VERSION]: (state: IBotState, version: number) => {
    state.liveVersion = version;
  },
  [Mutations.SET_LAST_VERSION]: (state: IBotState, version: IVersion) => {
    state.lastVersion = version;
  }
}